import type { ReportColumnsSpec, ReportMetricRow } from "@core/components/ReportTreeTable";
import type {
  PastorChurchEntry,
  PastorLeaf,
  PastorNationDistrictEntry,
  PastorSection,
  PastorTotals,
} from "@core/reports/api/fetchPastorReport";

/**
 * Adapters that turn the pastor endpoints' nested payload into the dynamic
 * ReportTreeTable contract — columns derived from `totals`, rows keyed by the
 * stable `field` (D1/D2/D16). No kpi_id matching anywhere.
 */

function isLeaf(v: unknown): v is PastorLeaf {
  return !!v && typeof v === "object" && "field" in (v as object) && "value" in (v as object);
}

/** Flatten a church's status/finances/statistics/assessment sections into { field: value }. */
function flattenChurchValues(church: PastorChurchEntry): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const sections: PastorSection[] = [church.status, church.finances, church.statistics, church.assessment];
  for (const section of sections) {
    if (!section) continue;
    for (const entry of Object.values(section)) {
      if (isLeaf(entry)) {
        out[entry.field] = entry.value;
      } else if (entry && typeof entry === "object") {
        // statistics rows nest e.g. { times_held: leaf, number_of_hours: leaf }
        for (const leaf of Object.values(entry as Record<string, PastorLeaf>)) {
          if (isLeaf(leaf)) out[leaf.field] = leaf.value;
        }
      }
    }
  }
  return out;
}

/** { field: value } from a totals object (for footer + district subtotal parent rows). */
export function pastorTotalsToFieldMap(totals: PastorTotals | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const entry of Object.values(totals ?? {})) {
    if (entry && entry.field) out[entry.field] = entry.value;
  }
  return out;
}

/** Build flat columns from the totals object (aggregatable KPIs only — D1/D3). */
export function pastorTotalsToColumnsSpec(totals: PastorTotals | undefined): ReportColumnsSpec {
  const columns = Object.values(totals ?? {})
    .filter((e) => !!e?.field)
    .map((e) => ({
      id: e.field,
      header: (e.label ?? "").replace(/^Total:\s*/i, ""),
      field: e.field,
    }));

  // Single unnamed group → flat row of columns.
  return { nameHeader: "", groups: [{ header: "", columns }] };
}

/** District scope: one row per church. */
export function buildPastorDistrictTree(churches: PastorChurchEntry[] | undefined): ReportMetricRow[] {
  return (churches ?? []).map((church, i) => ({
    id: `church:${church.church_id ?? i}`,
    name: church.church_name ?? `Church ${church.church_id ?? i}`,
    church_id: church.church_id,
    church_name: church.church_name,
    department_report_id: church.department_submission?.department_report_id ?? null,
    report_status_name: church.department_submission?.report_status_name ?? null,
    media: church.media ?? [],
    ...flattenChurchValues(church),
  }));
}

/** Nation scope: district parent rows (subtotals) → church sub-rows. */
export function buildPastorNationTree(districts: PastorNationDistrictEntry[] | undefined): ReportMetricRow[] {
  return (districts ?? []).map((district, di) => ({
    id: `district:${district.district_id ?? di}`,
    name: district.district_name ?? `District ${district.district_id ?? di}`,
    district_id: district.district_id,
    district_name: district.district_name,
    department_report_id: district.department_submission?.department_report_id ?? null,
    report_status_name: district.department_submission?.report_status_name ?? null,
    media: district.media ?? [],
    // District subtotal values (numerics are re-summed natively by the table; booleans show
    // Yes/No-blank per D17 — authoritative counts are the footer + these API subtotals).
    ...pastorTotalsToFieldMap(district.totals),
    subRows: (district.churches ?? []).map((church, ci) => ({
      id: `district:${district.district_id ?? di}:church:${church.church_id ?? ci}`,
      name: church.church_name ?? `Church ${church.church_id ?? ci}`,
      church_id: church.church_id,
      church_name: church.church_name,
      department_report_id: church.department_submission?.department_report_id ?? null,
      report_status_name: church.department_submission?.report_status_name ?? null,
      media: church.media ?? [],
      ...flattenChurchValues(church),
    })),
  }));
}
